import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { Reveal, MaskLines } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD =
  "w-full bg-transparent border-0 border-b border-foreground/20 rounded-none px-0 py-4 text-lg text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-primary transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", budget: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please add your name, email and a message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-10 lg:py-14 px-6 lg:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <p className="inline-block bg-primary text-primary-foreground px-4 py-2 text-xs sm:text-sm uppercase tracking-[0.25em] font-bold mb-8">Start a project</p>
          <MaskLines
            lines={["Let's Create", <>Some <span className="font-accent text-primary">Magic</span></>]}
            className="font-display font-extrabold text-5xl sm:text-6xl lg:text-7xl uppercase tracking-tighter leading-[0.88]"
            stagger={0.1}
          />
          <p className="mt-8 font-body text-base lg:text-lg text-foreground/60 max-w-md">
            Tell us what you&apos;re building. Brand, film or both — we&apos;ll bring the order to your chaos.
          </p>
          <div className="mt-10 flex flex-col gap-2 text-foreground/70">
            <a
              data-testid="contact-info-email"
              href="mailto:sixesandsevens01@gmail.com"
              className="link-underline inline-block w-fit text-lg"
            >
              sixesandsevens01@gmail.com
            </a>
            <a
              data-testid="contact-info-phone"
              href="tel:+919324490236"
              className="link-underline inline-block w-fit text-lg"
            >
              +91 93244 90236
            </a>
            <p className="mt-2 text-sm text-foreground/40 uppercase tracking-[0.2em]">Mumbai · Worldwide</p>
          </div>
        </div>

        <Reveal className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input data-testid="contact-name" className={FIELD} placeholder="Your name *" value={form.name} onChange={update("name")} />
              <input data-testid="contact-email" type="email" className={FIELD} placeholder="Email *" value={form.email} onChange={update("email")} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <input data-testid="contact-company" className={FIELD} placeholder="Company" value={form.company} onChange={update("company")} />
              <input data-testid="contact-budget" className={FIELD} placeholder="Budget range" value={form.budget} onChange={update("budget")} />
            </div>
            <textarea data-testid="contact-message" rows={4} className={`${FIELD} resize-none`} placeholder="Tell us about the project *" value={form.message} onChange={update("message")} />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="group inline-flex items-center gap-4 border border-foreground/20 px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors duration-300 disabled:opacity-50"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : "Send enquiry"}
              {!loading && <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
